// Utils
import { cloneDeep } from 'lodash';

// Constants
import PaymentPlatforms from 'constants/PaymentPlatforms';

/**
 * registeredFields is used in PracticeDataView and PracticeEditView
 * to render JSX elements.
 *
 * You can opt out of the default render mechanism, but still pass data
 * down to these two components using this key / value pair:
 *      mapToJSX: false
 */
export const paymentOptionsRegisteredFields = {
  contactEmail: {
    mapToJSX: true,
    name: 'contactEmail',
    header: 'receipt email',
    editable: true,
    inputEl: 'input',
    type: 'email',
    placeholder: 'e.g.: mail@mydomain.com',
  },
  payFabricSetupId: {
    mapToJSX: false,
    name: 'payFabricSetupId',
    header: 'payfabric setup id',
    editable: false,
    inputEl: 'input',
    type: 'text',
  },
  payFabricDeviceId: {
    mapToJSX: false,
    name: 'payFabricDeviceId',
    header: 'payfabric device id',
    editable: false,
    inputEl: 'input',
    type: 'text',
  },
  paymentLinkEnabled: {
    // essentially just enables the Text and Email to pay options in the dropdown
    mapToJSX: false,
    name: 'paymentLinkEnabled',
    header: 'allow text/email to pay links',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  onlyRxHeroPayments: {
    // essentially just disables the New Payment options
    mapToJSX: false,
    name: 'onlyRxHeroPayments',
    header: 'only rxhero payments',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  paymentPlatform: {
    mapToJSX: true,
    name: 'paymentPlatform',
    header: 'payment platform',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'PAYFABRIC', value: PaymentPlatforms.payfabric },
      { label: 'GLOBALPAY', value: PaymentPlatforms.globalpay },
    ],
  },
  paymentLinkCustomMessage: {
    mapToJSX: false,
    name: 'paymentLinkCustomMessage',
    header: 'payment request custom message',
    editable: true,
    inputEl: 'button',
    // When inputEl === 'button' there should also be a handleClick property
    // For this particular button the handleClick is defined later
  },
  paymentLinkExpiration: {
    mapToJSX: true,
    name: 'paymentLinkExpiration',
    header: 'payment request expiration (in hours)',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter value',
  },
  payWithSavedCards: {
    mapToJSX: true,
    name: 'payWithSavedCards',
    header: 'Pay With Saved Cards',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  paymentLinkEmailUpdateEnabled: {
    // sends out a receipt to their email afterwards
    mapToJSX: true,
    name: 'paymentLinkEmailUpdateEnabled',
    header: 'payment link automatic receipt emails',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  allowSyncerWriteback: {
    mapToJSX: true,
    name: 'allowSyncerWriteback',
    header: 'allow syncer writeback',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  paymentLandingPage: {
    // right now this is just used for the /pay-direct PaymentWidget code
    mapToJSX: true,
    name: 'paymentLandingPage',
    header: 'PayFabric Payment Landing Page',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
};

export const coreDataKeys = [
  'address1',
  'address2',
  'city',
  'state',
  'zip',
  'logoUrl',
];

export const coreDataRegisteredFields = {
  address1: {
    mapToJSX: true,
    name: 'address1',
    header: 'address line 1',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter address line 1',
  },
  address2: {
    mapToJSX: true,
    name: 'address2',
    header: 'address line 2',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter address line 2',
  },
  city: {
    mapToJSX: true,
    name: 'city',
    header: 'city',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter city',
  },
  state: {
    mapToJSX: true,
    name: 'state',
    header: 'state',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter state',
  },
  zip: {
    mapToJSX: true,
    name: 'zip',
    header: 'zip',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter zip',
  },
  logoUrl: {
    mapToJSX: true,
    name: 'logoUrl',
    header: 'logo url',
    editable: true,
    inputEl: 'input',
    type: 'text',
    placeholder: 'Enter logo url',
  },
};

export const paxDeviceRegisteredFields = {
  requireStaffMember: {
    mapToJSX: true,
    name: 'requireStaffMember',
    header: 'staff member required',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  requirePetOwnerName: {
    mapToJSX: true,
    name: 'requirePetOwnerName',
    header: 'pet owner name required',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  requireDescription: {
    mapToJSX: true,
    name: 'requireDescription',
    header: 'description required',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  requireRefundReason: {
    mapToJSX: true,
    name: 'requireRefundReason',
    header: 'refund reason required',
    editable: true,
    inputEl: 'input',
    type: 'radio',
    choices: [
      { label: 'YES', value: true },
      { label: 'NO', value: false },
    ],
  },
  mobileSignatureCaptureMinimumAmount: {
    mapToJSX: true,
    name: 'mobileSignatureCaptureMinimumAmount',
    header: 'signature capture minimum amount',
    editable: true,
    inputEl: 'input',
    type: 'text',
    icon: 'fas fa-dollar-sign',
    prependIcon: true,
  },
};

export function updateRegisteredFields(practice, fieldsObj) {
  const copy = cloneDeep(fieldsObj);

  for (let key in copy) {
    if (isMobileRequirementField(key)) {
      copy[key].val = practice?.mobileRequiredFields
        ? practice.mobileRequiredFields[key]
        : false;
    } else if (key === 'payFabricSetupId') {
      copy[key].val =
        practice?.payFabricSetupIds?.defaultSetupId ||
        practice?.payFabricSetupIds?.savedCardSetupId ||
        practice?.payFabricSetupIds?.terminalSetupId;
    } else if (key === 'payFabricDeviceId') {
      copy[key].val =
        practice?.payFabricDeviceIds?.defaultDeviceId ||
        practice?.payFabricDeviceIds?.savedCardDeviceId ||
        practice?.payFabricDeviceIds?.terminalDeviceId;
    } else if (coreDataKeys.includes(key)) {
      copy[key].val = practice?.coreData?.[key] ?? '';
    } else {
      copy[key].val = practice[key];
    }
  }

  return copy;
}

export function getValuesForBasicDetails(practice, propNames) {
  const mainDetails = [];

  for (let name of propNames) {
    let value = practice[name];

    if (name === 'active') {
      value = value ? 'Active' : 'Disabled';
    }

    mainDetails.push([name, value]);
  }

  return mainDetails;
}

export function isMobileRequirementField(field) {
  const mobileRequiredFields = [
    'requireStaffMember',
    'requirePetOwnerName',
    'requireDescription',
    'requireRefundReason',
  ];

  return mobileRequiredFields.includes(field);
}
